import { Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { UnifiedButton } from '../../ui/unified-button';

export interface ConfirmDialogAction {
  label: ReactNode;
  onClick: () => void;
  variant?: 'default' | 'outline';
  disabled?: boolean;
  icon?: ReactNode;
}

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  /** Slot for additional content below the description (e.g. warning banners, detail lists) */
  children?: ReactNode;
  /** Extra secondary actions rendered before the confirm button (e.g. export PDF / CSV) */
  extraActions?: ConfirmDialogAction[];
  onConfirm: () => void;
  confirmLabel?: ReactNode;
  confirmLoadingLabel?: string;
  isPending?: boolean;
  cancelLabel?: string;
  onCancel?: () => void;
  className?: string;
  /** Visual style for the confirm button. Use 'destructive' for irreversible actions. */
  confirmActionType?: 'submit' | 'destructive';
  'data-testid'?: string;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  extraActions,
  onConfirm,
  confirmLabel = 'Confirmar',
  confirmLoadingLabel = 'Guardando...',
  isPending = false,
  cancelLabel = 'Cancelar',
  onCancel,
  className,
  confirmActionType = 'submit',
  'data-testid': dataTestId,
}: ConfirmDialogProps) {
  const handleCancel = () => {
    if (isPending) return;
    onCancel?.();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !isPending && onOpenChange(v)}>
      <DialogContent className={className ?? 'sm:max-w-[28rem]'} data-testid={dataTestId}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && (
            <DialogDescription asChild={typeof description !== 'string'}>
              {description}
            </DialogDescription>
          )}
        </DialogHeader>

        {children && <div className="py-1">{children}</div>}

        <DialogFooter>
          <UnifiedButton
            buttonType="basic"
            variant="default"
            onClick={handleCancel}
            disabled={isPending}
            data-testid={dataTestId ? `${dataTestId}-cancel` : undefined}
          >
            {cancelLabel}
          </UnifiedButton>

          {extraActions?.map((action, index) => (
            // react-doctor-disable-next-line react-doctor/no-array-index-as-key -- static action list with stateless children; label is ReactNode, not a valid key
            <UnifiedButton
              key={index}
              buttonType="basic"
              variant={action.variant ?? 'outline'}
              onClick={action.onClick}
              disabled={action.disabled ?? isPending}
            >
              {action.icon && <span className="mr-2 flex items-center">{action.icon}</span>}
              {action.label}
            </UnifiedButton>
          ))}

          <UnifiedButton
            buttonType="basic"
            variant="outline"
            actionType={confirmActionType}
            onClick={onConfirm}
            disabled={isPending}
            data-testid={dataTestId ? `${dataTestId}-confirm` : undefined}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {confirmLoadingLabel}
              </>
            ) : (
              confirmLabel
            )}
          </UnifiedButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
